// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\MachineCatalog.jsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../store/appContext";
import "../styles/machineCatalog.css";

const MACHINE_STATUSES = [
  ["ACTIVA", "Activa"],
  ["FUERA_DE_SERVICIO", "Fuera de servicio"],
  ["BAJA", "Baja"],
];

const CRITICALITIES = [
  ["SIN_CLASIFICAR", "Sin clasificar"],
  ["BAJA", "Baja"],
  ["MEDIA", "Media"],
  ["ALTA", "Alta"],
  ["CRITICA", "Crítica"],
];

const EMPTY_FILTERS = { q: "", factory_unit_id: "", status: "", criticality: "" };
const EMPTY_UNIT = { unit_code: "", unit_name: "", unit_type: "LINEA", description: "", is_active: true };
const EMPTY_MACHINE = {
  machine_code: "",
  machine_name: "",
  factory_unit_id: "",
  manufacturer: "",
  model: "",
  serial_number: "",
  location_description: "",
  criticality: "SIN_CLASIFICAR",
  status: "ACTIVA",
  notes: "",
};

const text = (value) => String(value ?? "").trim();
const optional = (value) => text(value) || null;
const integer = (value) => new Intl.NumberFormat("es-ES").format(Number(value || 0));
const labelFrom = (options, value) => options.find(([code]) => code === value)?.[1] || value || "Sin dato";
const errorMessage = (result, fallback) => result?.error || result?.message || fallback;

const itemsFrom = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of ["items", "results", ...keys]) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const pageFrom = (payload, page, perPage) => {
  const meta = payload?.pagination || payload?.meta || {};
  const total = Number(meta.total ?? meta.total_items ?? payload?.total ?? 0);
  const totalPages = Number(meta.total_pages ?? meta.pages ?? payload?.total_pages ?? Math.max(1, Math.ceil(total / perPage)));
  return {
    page: Number(meta.page ?? payload?.page ?? page) || page,
    perPage: Number(meta.per_page ?? meta.page_size ?? perPage) || perPage,
    total: Number.isFinite(total) ? total : 0,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
  };
};

const Modal = ({ title, subtitle, children, footer, onClose, wide = false }) => (
  <div className="machine-modal-backdrop" role="presentation">
    <section className={`machine-modal${wide ? " machine-modal--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
      <header className="machine-modal__header">
        <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
      </header>
      <div className="machine-modal__body">{children}</div>
      {footer && <footer className="machine-modal__footer">{footer}</footer>}
    </section>
  </div>
);

const machinePayloadRoots = (value) => {
  const roots = [];
  const append = (candidate) => {
    if (
      candidate !== null &&
      candidate !== undefined &&
      !roots.includes(candidate)
    ) {
      roots.push(candidate);
    }
  };

  append(value);

  if (value && typeof value === "object" && !Array.isArray(value)) {
    append(value.data);
    append(value.payload);
    append(value.result);
  }

  [...roots].forEach((candidate) => {
    if (
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate)
    ) {
      append(candidate.data);
      append(candidate.payload);
      append(candidate.result);
    }
  });

  return roots;
};

const machineItemsOf = (value) => {
  const candidates = [];

  machinePayloadRoots(value).forEach((root) => {
    if (Array.isArray(root)) {
      candidates.push(root);
      return;
    }

    if (!root || typeof root !== "object") return;

    candidates.push(
      root.items,
      root.machines,
      root.rows,
      root.results,
      root.data,
    );
  });

  return candidates.find(Array.isArray) || [];
};

const machineSummaryOf = (value) => {
  const roots = machinePayloadRoots(value);
  const candidates = [];

  roots.forEach((root) => {
    if (!root || typeof root !== "object" || Array.isArray(root)) {
      return;
    }

    candidates.push(
      root.summary,
      root.status_summary,
      root.status_counts,
      root.counts,
      root.meta?.summary,
    );
  });

  const raw =
    candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate),
    ) || {};

  const pagination =
    roots
      .map((root) =>
        root && typeof root === "object" && !Array.isArray(root)
          ? root.pagination
          : null,
      )
      .find(
        (candidate) =>
          candidate &&
          typeof candidate === "object" &&
          !Array.isArray(candidate),
      ) || {};

  const statusMap =
    raw.statuses &&
    typeof raw.statuses === "object" &&
    !Array.isArray(raw.statuses)
      ? raw.statuses
      : {};

  return {
    ...raw,
    total:
      raw.total ??
      raw.total_count ??
      pagination.total ??
      0,
    active:
      raw.active ??
      raw.active_count ??
      raw.activa ??
      raw.ACTIVA ??
      statusMap.ACTIVA ??
      0,
    out_of_service:
      raw.out_of_service ??
      raw.out_of_service_count ??
      raw.fuera_de_servicio ??
      raw.FUERA_DE_SERVICIO ??
      statusMap.FUERA_DE_SERVICIO ??
      0,
    retired:
      raw.retired ??
      raw.retired_count ??
      raw.baja ??
      raw.BAJA ??
      statusMap.BAJA ??
      0,
  };
};

const MachineCatalog = () => {
  const { store, actions } = useContext(Context);
  const isAdmin = store?.role === "admin";
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [unitsError, setUnitsError] = useState("");
  const [machines, setMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [machinesError, setMachinesError] = useState("");
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [unitManagerOpen, setUnitManagerOpen] = useState(false);
  const [unitEditorOpen, setUnitEditorOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState(EMPTY_UNIT);
  const [unitError, setUnitError] = useState("");
  const [unitSubmitting, setUnitSubmitting] = useState(false);
  const [machineEditorOpen, setMachineEditorOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [machineForm, setMachineForm] = useState(EMPTY_MACHINE);
  const [machineError, setMachineError] = useState("");
  const [machineSubmitting, setMachineSubmitting] = useState(false);
  const machineRequest = useRef(0);

  const activeUnits = useMemo(() => units.filter((unit) => unit?.is_active !== false), [units]);

  const loadUnits = useCallback(async () => {
    setUnitsLoading(true);
    setUnitsError("");
    const result = await actions.getFactoryUnits({ page: 1, per_page: 100, sort_by: "unit_name", sort_order: "asc" });
    if (!result?.success) {
      setUnits([]);
      setUnitsError(errorMessage(result, "No se pudieron cargar las líneas y sectores"));
    } else {
      setUnits(itemsFrom(result.data, ["factory_units", "units"]));
    }
    setUnitsLoading(false);
  }, [actions]);

  const loadMachines = useCallback(async (signal) => {
    const requestId = machineRequest.current + 1;
    machineRequest.current = requestId;
    setMachinesLoading(true);
    setMachinesError("");
    const params = { page, per_page: pagination.perPage, sort_by: "machine_code", sort_order: "asc" };
    Object.entries(appliedFilters).forEach(([key, value]) => { if (text(value)) params[key] = text(value); });
    const result = await actions.getMachines(params, { signal });
    if (signal?.aborted || machineRequest.current !== requestId) return;
    if (!result?.success) {
      if (!result?.aborted) {
        setMachines([]);
        setMachinesError(errorMessage(result, "No se pudo cargar el catálogo de máquinas"));
      }
    } else {
      const payload = result.data || {};
      setMachines(machineItemsOf(result?.data));
      setSummary(machineSummaryOf(result?.data));
      setPagination((current) => pageFrom(payload, page, current.perPage));
    }
    setMachinesLoading(false);
  }, [actions, appliedFilters, page, pagination.perPage]);

  useEffect(() => { loadUnits(); }, [loadUnits]);
  useEffect(() => {
    const controller = new AbortController();
    loadMachines(controller.signal);
    return () => controller.abort();
  }, [loadMachines]);

  const applyFilters = (event) => { event.preventDefault(); setAppliedFilters({ ...draftFilters }); setPage(1); };
  const clearFilters = () => { setDraftFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS); setPage(1); };

  const openUnit = (unit = null) => {
    setEditingUnit(unit);
    setUnitForm(unit ? {
      unit_code: unit.unit_code || "",
      unit_name: unit.unit_name || "",
      unit_type: unit.unit_type || "LINEA",
      description: unit.description || "",
      is_active: unit.is_active !== false,
    } : EMPTY_UNIT);
    setUnitError("");
    setUnitEditorOpen(true);
  };

  const saveUnit = async (event) => {
    event.preventDefault();
    if (!text(unitForm.unit_code) || !text(unitForm.unit_name)) {
      setUnitError("Código y nombre son obligatorios");
      return;
    }
    setUnitSubmitting(true);
    setUnitError("");
    const payload = {
      unit_code: text(unitForm.unit_code),
      unit_name: text(unitForm.unit_name),
      unit_type: unitForm.unit_type,
      description: optional(unitForm.description),
      is_active: Boolean(unitForm.is_active),
    };
    const result = editingUnit
      ? await actions.updateFactoryUnit(editingUnit.id, payload)
      : await actions.createFactoryUnit(payload);
    if (!result?.success) {
      setUnitError(errorMessage(result, "No se pudo guardar la línea o sector"));
      setUnitSubmitting(false);
      return;
    }
    await loadUnits();
    setUnitSubmitting(false);
    setUnitEditorOpen(false);
  };

  const openMachine = (machine = null) => {
    setEditingMachine(machine);
    setMachineForm(machine ? {
      machine_code: machine.machine_code || "",
      machine_name: machine.machine_name || "",
      factory_unit_id: machine.factory_unit_id || machine.factory_unit?.id || "",
      manufacturer: machine.manufacturer || "",
      model: machine.model || "",
      serial_number: machine.serial_number || "",
      location_description: machine.location_description || "",
      criticality: machine.criticality || "SIN_CLASIFICAR",
      status: machine.status || "ACTIVA",
      notes: machine.notes || "",
    } : EMPTY_MACHINE);
    setMachineError("");
    setMachineEditorOpen(true);
  };

  const saveMachine = async (event) => {
    event.preventDefault();
    if (!text(machineForm.machine_code) || !text(machineForm.machine_name) || !text(machineForm.factory_unit_id)) {
      setMachineError("Código, nombre y línea/sector son obligatorios");
      return;
    }
    setMachineSubmitting(true);
    setMachineError("");
    const payload = {
      machine_code: text(machineForm.machine_code),
      machine_name: text(machineForm.machine_name),
      factory_unit_id: Number(machineForm.factory_unit_id),
      manufacturer: optional(machineForm.manufacturer),
      model: optional(machineForm.model),
      serial_number: optional(machineForm.serial_number),
      location_description: optional(machineForm.location_description),
      criticality: machineForm.criticality,
      status: machineForm.status,
      notes: optional(machineForm.notes),
    };
    const result = editingMachine
      ? await actions.updateMachine(editingMachine.id, payload)
      : await actions.createMachine(payload);
    if (!result?.success) {
      setMachineError(errorMessage(result, "No se pudo guardar la máquina"));
      setMachineSubmitting(false);
      return;
    }
    setMachineSubmitting(false);
    setMachineEditorOpen(false);
    await loadMachines();
  };

  const statuses = editingMachine?.status === "BAJA"
    ? MACHINE_STATUSES.filter(([code]) => code !== "ACTIVA")
    : MACHINE_STATUSES;

  const total = summary.total ?? summary.total_machines ?? pagination.total;
  const active = summary.active ?? summary.active_count ?? summary.activa ?? 0;
  const stopped = summary.out_of_service ?? summary.out_of_service_count ?? summary.fuera_de_servicio ?? 0;
  const retired = summary.retired ?? summary.retired_count ?? summary.baja ?? 0;

  return (
    <main className="machine-catalog-page">
      <header className="machine-catalog-header">
        <div>
          <p className="machine-catalog-eyebrow">Catálogo técnico</p>
          <h1>Máquinas</h1>
          <p>Consulta líneas, sectores y máquinas de fábrica. Abre una máquina para ver su ficha y gestionar los repuestos asociados.</p>
        </div>
        {isAdmin && (
          <div className="machine-catalog-actions">
            <button type="button" className="btn btn-outline-primary" onClick={() => setUnitManagerOpen(true)}>Líneas y sectores</button>
            <button type="button" className="btn btn-primary" onClick={() => openMachine()} disabled={!activeUnits.length}>Nueva máquina</button>
          </div>
        )}
      </header>

      <section className="machine-summary-grid" aria-label="Resumen de máquinas">
        {[['Total', total], ['Activas', active], ['Fuera de servicio', stopped], ['Bajas', retired]].map(([label, value]) => (
          <article className="machine-summary-card" key={label}><span>{label}</span><strong>{integer(value)}</strong></article>
        ))}
      </section>

      <form className="machine-filter-card" onSubmit={applyFilters}>
        <div className="machine-filter-grid">
          <label>Buscar<input className="form-control" type="search" value={draftFilters.q} onChange={(e) => setDraftFilters((current) => ({ ...current, q: e.target.value }))} placeholder="Código, nombre, fabricante, modelo, serie o ubicación" /></label>
          <label>Línea o sector<select className="form-select" value={draftFilters.factory_unit_id} onChange={(e) => setDraftFilters((current) => ({ ...current, factory_unit_id: e.target.value }))}><option value="">Todas</option>{units.map((unit) => <option key={unit.id} value={unit.id}>{unit.unit_code} · {unit.unit_name}</option>)}</select></label>
          <label>Estado<select className="form-select" value={draftFilters.status} onChange={(e) => setDraftFilters((current) => ({ ...current, status: e.target.value }))}><option value="">Todos</option>{MACHINE_STATUSES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
          <label>Criticidad<select className="form-select" value={draftFilters.criticality} onChange={(e) => setDraftFilters((current) => ({ ...current, criticality: e.target.value }))}><option value="">Todas</option>{CRITICALITIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
        </div>
        <div className="machine-filter-actions"><button className="btn btn-primary" type="submit">Aplicar filtros</button><button className="btn btn-outline-secondary" type="button" onClick={clearFilters}>Limpiar</button></div>
      </form>

      {unitsError && <div className="alert alert-warning">{unitsError} <button type="button" className="btn btn-sm btn-outline-dark" onClick={loadUnits}>Reintentar</button></div>}

      <section className="machine-list-card">
        <header><div><h2>Máquinas registradas</h2><p>{machinesLoading ? "Actualizando…" : `${integer(pagination.total)} resultados`}</p></div></header>
        {machinesError && <div className="machine-state machine-state--error"><p>{machinesError}</p><button type="button" className="btn btn-outline-primary" onClick={() => loadMachines()}>Reintentar</button></div>}
        {!machinesError && machinesLoading && <div className="machine-state"><div className="spinner-border text-primary" role="status" /><p>Cargando catálogo…</p></div>}
        {!machinesError && !machinesLoading && !machines.length && <div className="machine-state"><h3>No hay máquinas para mostrar</h3><p>Ajusta los filtros o registra la primera máquina.</p></div>}
        {!machinesError && !machinesLoading && machines.length > 0 && (
          <>
            <div className="machine-table-wrap"><table className="table machine-table"><thead><tr><th>Código</th><th>Máquina</th><th>Línea / sector</th><th>Fabricante / modelo</th><th>Serie</th><th>Ubicación</th><th>Criticidad</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>
              {machines.map((machine) => {
                const unit = machine.factory_unit || units.find((item) => Number(item.id) === Number(machine.factory_unit_id));
                return <tr key={machine.id}>
                  <td data-label="Código" className="machine-code">{machine.machine_code || "Sin código"}</td>
                  <td data-label="Máquina"><strong>{machine.machine_name || "Sin nombre"}</strong></td>
                  <td data-label="Línea / sector">{unit ? `${unit.unit_code} · ${unit.unit_name}` : "Sin unidad"}</td>
                  <td data-label="Fabricante / modelo">{machine.manufacturer || "Sin dato"}<small>{machine.model || "Sin modelo"}</small></td>
                  <td data-label="Serie">{machine.serial_number || "Sin dato"}</td>
                  <td data-label="Ubicación">{machine.location_description || "Sin dato"}</td>
                  <td data-label="Criticidad"><span className={`machine-badge machine-badge--criticality-${String(machine.criticality || "SIN_CLASIFICAR").toLowerCase()}`}>{labelFrom(CRITICALITIES, machine.criticality)}</span></td>
                  <td data-label="Estado"><span className={`machine-badge machine-badge--status-${String(machine.status || "ACTIVA").toLowerCase()}`}>{labelFrom(MACHINE_STATUSES, machine.status)}</span></td>
                  <td data-label="Acciones">
                    <div className="machine-row-actions">
                      <a className="btn btn-sm btn-primary" href={`/machines/${machine.id}`}>Ver ficha</a>
                      {isAdmin && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openMachine(machine)}>Editar</button>}
                    </div>
                  </td>
                </tr>;
              })}
            </tbody></table></div>
            <nav className="machine-pagination" aria-label="Paginación de máquinas"><button type="button" className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</button><span>Página {pagination.page} de {pagination.totalPages}</span><button type="button" className="btn btn-outline-secondary" disabled={page >= pagination.totalPages} onClick={() => setPage((value) => Math.min(pagination.totalPages, value + 1))}>Siguiente</button></nav>
          </>
        )}
      </section>

      {unitManagerOpen && <Modal title="Líneas y sectores" subtitle={`${unitsLoading ? "Cargando" : integer(units.length)} unidades`} onClose={() => setUnitManagerOpen(false)} wide footer={<button type="button" className="btn btn-primary" onClick={() => openUnit()}>Nueva unidad</button>}>
        <div className="machine-unit-list">{units.map((unit) => <article className="machine-unit-row" key={unit.id}><div><strong>{unit.unit_code} · {unit.unit_name}</strong><span>{unit.unit_type === "LINEA" ? "Línea" : "Sector"} · {unit.is_active === false ? "Inactiva" : "Activa"}</span></div><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openUnit(unit)}>Editar</button></article>)}</div>
      </Modal>}

      {unitEditorOpen && <Modal title={editingUnit ? "Editar línea o sector" : "Nueva línea o sector"} onClose={() => !unitSubmitting && setUnitEditorOpen(false)} footer={<><button type="button" className="btn btn-outline-secondary" onClick={() => setUnitEditorOpen(false)} disabled={unitSubmitting}>Cancelar</button><button type="submit" form="unit-form" className="btn btn-primary" disabled={unitSubmitting}>{unitSubmitting ? "Guardando…" : "Guardar"}</button></>}>
        <form id="unit-form" className="machine-form-grid" onSubmit={saveUnit}>
          <label>Código<input className="form-control" value={unitForm.unit_code} onChange={(e) => setUnitForm((current) => ({ ...current, unit_code: e.target.value }))} /></label>
          <label>Nombre<input className="form-control" value={unitForm.unit_name} onChange={(e) => setUnitForm((current) => ({ ...current, unit_name: e.target.value }))} /></label>
          <label>Tipo<select className="form-select" value={unitForm.unit_type} onChange={(e) => setUnitForm((current) => ({ ...current, unit_type: e.target.value }))}><option value="LINEA">Línea</option><option value="SECTOR">Sector</option></select></label>
          <label className="machine-field--full">Descripción<textarea className="form-control" rows="3" value={unitForm.description} onChange={(e) => setUnitForm((current) => ({ ...current, description: e.target.value }))} /></label>
          {editingUnit && <label className="machine-check machine-field--full"><input type="checkbox" checked={unitForm.is_active} onChange={(e) => setUnitForm((current) => ({ ...current, is_active: e.target.checked }))} /> Unidad activa</label>}
          {unitError && <div className="alert alert-danger machine-field--full">{unitError}</div>}
        </form>
      </Modal>}

      {machineEditorOpen && <Modal title={editingMachine ? "Editar máquina" : "Nueva máquina"} subtitle="Los datos no disponibles pueden completarse posteriormente." onClose={() => !machineSubmitting && setMachineEditorOpen(false)} wide footer={<><button type="button" className="btn btn-outline-secondary" onClick={() => setMachineEditorOpen(false)} disabled={machineSubmitting}>Cancelar</button><button type="submit" form="machine-form" className="btn btn-primary" disabled={machineSubmitting}>{machineSubmitting ? "Guardando…" : "Guardar máquina"}</button></>}>
        <form id="machine-form" className="machine-form-grid" onSubmit={saveMachine}>
          <label>Código<input className="form-control" value={machineForm.machine_code} onChange={(e) => setMachineForm((current) => ({ ...current, machine_code: e.target.value }))} /></label>
          <label>Nombre<input className="form-control" value={machineForm.machine_name} onChange={(e) => setMachineForm((current) => ({ ...current, machine_name: e.target.value }))} /></label>
          <label>Línea o sector<select className="form-select" value={machineForm.factory_unit_id} onChange={(e) => setMachineForm((current) => ({ ...current, factory_unit_id: e.target.value }))}><option value="">Seleccionar</option>{activeUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.unit_code} · {unit.unit_name}</option>)}</select></label>
          <label>Estado<select className="form-select" value={machineForm.status} onChange={(e) => setMachineForm((current) => ({ ...current, status: e.target.value }))}>{statuses.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select>{editingMachine?.status === "BAJA" && <small>Para reactivar, guarda primero como ?Fuera de servicio?.</small>}</label>
          <label>Fabricante<input className="form-control" value={machineForm.manufacturer} onChange={(e) => setMachineForm((current) => ({ ...current, manufacturer: e.target.value }))} /></label>
          <label>Modelo<input className="form-control" value={machineForm.model} onChange={(e) => setMachineForm((current) => ({ ...current, model: e.target.value }))} /></label>
          <label>Número de serie<input className="form-control" value={machineForm.serial_number} onChange={(e) => setMachineForm((current) => ({ ...current, serial_number: e.target.value }))} /><small>Puede repetirse.</small></label>
          <label>Ubicación<input className="form-control" value={machineForm.location_description} onChange={(e) => setMachineForm((current) => ({ ...current, location_description: e.target.value }))} /></label>
          <label>Criticidad<select className="form-select" value={machineForm.criticality} onChange={(e) => setMachineForm((current) => ({ ...current, criticality: e.target.value }))}>{CRITICALITIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
          <label className="machine-field--full">Notas<textarea className="form-control" rows="3" value={machineForm.notes} onChange={(e) => setMachineForm((current) => ({ ...current, notes: e.target.value }))} /></label>
          {machineError && <div className="alert alert-danger machine-field--full">{machineError}</div>}
        </form>
      </Modal>}
    </main>
  );
};

export default MachineCatalog;
